import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Terminal, Lock, Shield, Key, Database, FileCheck, Globe, Cpu, Download,
  ChevronLeft, ArrowRight, Check, Mail, PhoneCall, CheckCircle2,
} from "lucide-react";
import usePageview from "@/lib/usePageview";
import BookCallDialog from "@/components/BookCallDialog";
import VideoAd from "@/components/VideoAd";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Phase 25.6a — Public Trust Center / Security page.
// First answer to every "is my data safe?" sales objection.

export default function Security() {
  usePageview("/security");
  const [callOpen, setCallOpen] = useState(false);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="security-page">
      {/* Top nav */}
      <header className="border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href={`${BACKEND_URL}/api/public/overview-one-pager`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="security-overview-pdf"
              className="border border-zinc-700 hover:border-[#FFB000]/50 text-zinc-300 hover:text-[#FFB000] px-3 py-2 font-mono text-[10px] uppercase tracking-widest hidden sm:inline-flex items-center gap-2"
              title="1-page DARA overview — no email required"
            >
              <Download className="w-3 h-3" /> DARA 1-PAGER
            </a>
            <a
              href={`${BACKEND_URL}/api/public/security-onepager.pdf`}
              data-testid="security-pdf-link"
              className="border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2"
            >
              <Download className="w-3 h-3" /> SECURITY PDF
            </a>
            <Link to="/login" className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2">
              LOGIN
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-900 py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-4">
            // SECURITY · TRUST CENTER
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Your data is yours.<br />
            <span className="text-[#FFB000]">Encrypted, scoped, never trained on.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mt-6 leading-relaxed">
            DARA was built for SMBs, hospitality operators, and enterprise teams
            who care more about their data than their dashboard. Here's exactly
            what we do — in plain English — so you can show this page to your
            IT person, accountant, or board in 60 seconds.
          </p>
          <div className="flex items-center gap-3 mt-8 flex-wrap">
            <button
              type="button"
              onClick={() => setCallOpen(true)}
              data-testid="security-hero-book-call"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2"
            >
              <PhoneCall className="w-3.5 h-3.5" /> BOOK 15-MIN SECURITY REVIEW
            </button>
            <a
              href={`${BACKEND_URL}/api/public/security-onepager.pdf`}
              data-testid="security-hero-pdf"
              className="border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 px-5 py-3 font-mono text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> DOWNLOAD ONE-PAGER
            </a>
            <a
              href="mailto:security@daraos.ai"
              data-testid="security-hero-mailto"
              className="border border-zinc-800 hover:border-zinc-700 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" /> SECURITY@DARAOS.AI
            </a>
          </div>
        </div>
      </section>

      {/* Phase 25.13au — Read-only architecture walk-through video.
          45-second visual explanation of HOW DARA protects data:
          read-only · AES-256 · TLS 1.3 · tenant-isolated. Shown right after
          the hero so prospects can SEE the architecture before reading the
          trust blocks below. Portrait-aspect source (9:16) — VideoAd
          auto-snaps the container to match and caps height at 80vh. */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // 45 SECONDS · HOW DARA HANDLES YOUR DATA
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-2">
            What we actually do with it — <span className="text-[#FFB000]">visualised.</span>
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-2xl">
            For anyone who wants the architecture in pictures rather than paragraphs. Read-only connectors, AES-256 at rest, TLS 1.3 in transit, tenant-isolated workspaces, zero training on your data. Same content as the security PDF — designed for the IT person or board member who has 45 seconds.
          </p>
          <VideoAd
            src="/videos/dara_security.mp4"
            poster="/videos/dara_security_poster.jpg"
            label="// READ-ONLY · ENCRYPTED · ZERO TRAINING"
            caption="DARA SECURITY ARCHITECTURE · 45 SECONDS · NO NARRATION · SUBTITLES ON-SCREEN"
            testId="security-video"
            maxHeight="75vh"
          />
        </div>
      </section>

      {/* Phase 25.13r — Vendor due-diligence CTA panel.
          Specifically for the enterprise-buyer cohort spending 2+ visits on
          /security; converts a passive read into a booked diligence call. */}
      <section className="border-b border-zinc-900 bg-[#0a0700]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="security-vendor-cta">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 border border-[#FFB000] bg-[#1a1300] flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-[#FFB000]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-1">
                // VENDOR DUE-DILIGENCE
              </div>
              <h3 className="font-heading font-black text-lg sm:text-xl tracking-tight mb-1.5">
                Doing IT review for your team? Get the answers in 15 minutes.
              </h3>
              <div className="font-mono text-[11px] text-zinc-400 leading-relaxed flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#00E676]" /> SOC 2 path</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#00E676]" /> DPA on request</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#00E676]" /> Data-residency Q&amp;A</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#00E676]" /> Live, recorded if asked</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCallOpen(true)}
            data-testid="security-vendor-cta-book"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            BOOK 15-MIN REVIEW <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>

      {/* 6 Trust Blocks */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TrustBlock
              icon={Lock}
              accent="#FFB000"
              label="ENCRYPTION"
              headline="AES-256 at rest, TLS 1.3 in transit"
              bullets={[
                "All OAuth tokens (Square, Xero, MYOB) encrypted at rest with AES-256 Fernet",
                "Platform API keys encrypted in a dedicated MongoDB vault — never logged",
                "Encryption keys derived from JWT_SECRET, rotated per environment",
                "TLS 1.3 with HSTS enforced on every public endpoint",
              ]}
              testid="block-encryption"
            />
            <TrustBlock
              icon={Shield}
              accent="#FFB000"
              label="ACCESS CONTROL"
              headline="Per-workspace isolation, optional 2FA"
              bullets={[
                "Every database query is scoped by workspace_id — your data never crosses tenant lines",
                "Passwords hashed with bcrypt (12 rounds, salted), never stored in plaintext",
                "Optional TOTP 2FA (Google Authenticator, 1Password, Authy)",
                "JWT bearer tokens with short TTL + re-issue on sensitive actions",
              ]}
              testid="block-access"
            />
            <TrustBlock
              icon={Key}
              accent="#FFB000"
              label="CONNECTOR SCOPES"
              headline="Read-only. We never write to your books."
              bullets={[
                "Xero: invoices.read · contacts.read · settings.read",
                "Square: payments.read · orders.read · merchant.read · items.read",
                "MYOB: sme-general-account · sme-general-contact · sme-general-sale",
                "Zero write scopes anywhere — DARA can never modify your accounting or POS data",
              ]}
              testid="block-scopes"
            />
            <TrustBlock
              icon={Cpu}
              accent="#00E676"
              label="AI & DATA USE"
              headline="Your data is NEVER used to train AI"
              bullets={[
                "Customer data is never used to fine-tune Anthropic, OpenAI, or any LLM",
                "DARA Briefings use RAG-style prompts only — your prompts go in, no learning comes out",
                "No third-party tracking pixels (Google Analytics, Meta Pixel, X tag)",
                "Anonymous network-org lookup via ip-api.com — only your IP is sent, only org name returned (no PII, no cookie). Country comes from a local GeoLite2 DB.",
              ]}
              testid="block-ai"
            />
            <TrustBlock
              icon={FileCheck}
              accent="#FFB000"
              label="YOUR RIGHTS"
              headline="Export & delete with one click"
              bullets={[
                "Export ALL your workspace data as JSON — Settings → Privacy → Export",
                "Permanently delete your workspace with typed confirmation — irreversible by design",
                "Disconnect any OAuth grant instantly — Sources page → Disconnect",
                "Full audit log of sensitive actions, visible to super-admins",
              ]}
              testid="block-rights"
            />
            <TrustBlock
              icon={Globe}
              accent="#FFB000"
              label="INFRASTRUCTURE"
              headline="Production-grade hosting + named subprocessors"
              bullets={[
                "Hosted on Emergent (Kubernetes), TLS terminated at the edge",
                "MongoDB multi-tenant by workspace_id, point-in-time backups",
                "Subprocessors disclosed: Anthropic (AI), Resend (email), Emergent (compute)",
                "SOC 2 Type II roadmap underway — happy to share status under NDA",
              ]}
              testid="block-infra"
            />
          </div>
        </div>
      </section>

      {/* Connector matrix */}
      <section className="border-y border-zinc-900 py-16 bg-zinc-950/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 text-center">
            // CONNECTOR PERMISSIONS · WHAT WE READ, WHAT WE WRITE
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-center mb-10">
            Every integration is read-only.
          </h2>
          <div className="overflow-x-auto border border-zinc-900 bg-black">
            <table className="w-full">
              <thead className="border-b border-zinc-900">
                <tr className="text-zinc-500 uppercase tracking-widest text-[10px] font-mono">
                  <th className="text-left px-4 py-3">Service</th>
                  <th className="text-left px-4 py-3">What DARA reads</th>
                  <th className="text-center px-4 py-3">Write access</th>
                  <th className="text-center px-4 py-3">Stored encrypted</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {[
                  ["Square POS", "Payments · Orders · Merchant profile · Items", false, true],
                  ["Xero", "Invoices · Contacts · Chart of accounts", false, true],
                  ["MYOB AccountRight", "Invoices · Customers · Accounts", false, true],
                  ["Webhook signatures", "HMAC-verified before persistence", false, true],
                ].map((row, i) => (
                  <tr key={i} className="border-b border-zinc-900">
                    <td className="px-4 py-3 text-white font-heading font-bold">{row[0]}</td>
                    <td className="px-4 py-3 text-zinc-400">{row[1]}</td>
                    <td className="px-4 py-3 text-center">
                      {!row[2] && <span className="text-[#FF6B47] font-bold">✗ NONE</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row[3] && <span className="text-[#00E676] inline-flex items-center gap-1"><Check className="w-3 h-3" /> AES-256</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA + contact */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            Questions your IT team or board need answered?
          </h2>
          <p className="text-base text-zinc-400 mt-4 leading-relaxed">
            We'll respond within 1 business day. Custom DPAs, security questionnaires,
            and pen-test summaries available on request once you've signed up for a trial.
          </p>
          <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
            <a
              href={`${BACKEND_URL}/api/public/security-onepager.pdf`}
              data-testid="security-footer-pdf"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> DOWNLOAD ONE-PAGER
            </a>
            <a
              href="mailto:security@daraos.ai?subject=DARA%20-%20Security%20question"
              className="border border-zinc-800 hover:border-zinc-700 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" /> EMAIL SECURITY TEAM
            </a>
          </div>
          <div className="mt-10">
            <Link to="/" className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> BACK TO HOMEPAGE
            </Link>
          </div>
        </div>
      </section>

      <BookCallDialog
        open={callOpen}
        defaultInterest="security"
        onClose={() => setCallOpen(false)}
      />
    </div>
  );
}

function TrustBlock({ icon: Icon, accent, label, headline, bullets, testid }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="border p-2.5" style={{ borderColor: accent + "55", backgroundColor: accent + "11" }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>
          // {label}
        </div>
      </div>
      <h3 className="font-heading font-black text-xl tracking-tight text-white mb-4">{headline}</h3>
      <ul className="space-y-2">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
            <Check className="w-3.5 h-3.5 text-[#00E676] flex-shrink-0 mt-1" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
